// Connected · EntitySummaryCard
//
// Hover-density popover body rendered inside `EntityLink`. One sub-component
// per `EntityType`; each fetches its own row via Refine `useOne` and projects
// it onto the @ds primitive stack:
//
//   Card (compact)           outer surface
//     ├─ Eyebrow              section label ("FIRMA", "FAKTURA", …)
//     ├─ Title row            entity name / reference (semibold, primary text)
//     ├─ Status pill row      Pill primitives — soft tone, sm size
//     ├─ KVGrid (rows)        meta rows — vertical, compact, peek-width
//     ├─ AmountDisplay        for monetary lines
//     ├─ EntityChip           for related-entity references (parent company,
//     │                        seller→buyer, places)
//     └─ EmptyValue           for null fields
//
// Width budget is the `EntityLink` popover overlay (`maxWidth: 360`), so KVGrid
// runs in `layout="rows"` mode and amounts use `size="sm"`/`"md"`.
//
// Every visual value flows through @ds primitives → `@ds/tokens/palette`.
// The legacy `commons/cards/Card`, `CardHeader`, `CardMetaGrid`, `Eyebrow`
// (legacy), `AmountDisplay` (legacy), `StaticTags`, `PriceCell`, and
// `ContactPurposesCompactTag` consumers are gone from this file.

import React from "react";
import { useOne, useList } from "@refinedev/core";
import { useTranslation } from "react-i18next";
import { ShopOutlined } from "@ant-design/icons";

import type { Company, Contact } from "@shared/types/company";
import type { Product } from "@shared/types/product";
import type { Transaction } from "@shared/types/transaction";
import type { InvoiceRow } from "@shared/types/invoice";
import type { Employee } from "@shared/types/employee";
import type { PriceList } from "@shared/types/price_list";

import { Box } from "@ds/layout/Box";
import { Card } from "@ds/layout/Card";
import { Stack } from "@ds/layout/Stack";
import { Inline } from "@ds/layout/Inline";
import { KVGrid, type KVItem } from "@ds/layout/KVGrid";
import { TextValue } from "@ds/display/TextValue";
import { Pill, type PillTone } from "@ds/display/Pill";
import { StatusChipShell } from "@ds/display/StatusChipShell";
import { SideChip } from "@ds/domain/SideChip";
import { EnumChip } from "@ds/domain/EnumChip";
import { DprChip } from "@ds/domain/DprChip";
import { Eyebrow } from "@ds/display/Eyebrow";
import { AmountDisplay } from "@ds/display/AmountDisplay";
import { EmptyValue } from "@ds/display/EmptyValue";
import { CopyableValue } from "@ds/display/CopyableValue";
import { EntityChip } from "@ds/display/EntityChip";
import {
  INVOICE_REVIEW_STATUS_COLORS,
  ROLE_COLORS,
  TRANSACTION_STATUS_COLORS,
} from "@ds/tokens/domainTones";

import { useFormat } from "@ds/display/useNumberFormat";
import { formatVatRateCode } from "@shared/calc";
import { formatPriceListRef } from "@ds/domain/formatPriceListRef";
import type { EntityType } from "@ds/navigation/navigation";

// ─── Popover width budget ────────────────────────────────────────────────────
//
// EntityLink's Popover sets `overlayStyle: { maxWidth: 360 }`. Anything we
// render must fit comfortably inside that — KVGrid runs in "rows" mode and we
// keep meta rows compact so the card never overflows.
//
// 320 is the minimum we render at to give labels + values some breathing room
// while staying well under the 360 ceiling.

const POPOVER_MIN_WIDTH = 320;

// ─── AntD-color → @ds tone mapping ───────────────────────────────────────────
//
// Status constants in `src/types/constants.ts` use AntD's colour names
// (`"green"`, `"warning"`, `"volcano"`, …). We map those onto the @ds
// `ToneName` palette. Anything unknown collapses to `grey`. The collapse is
// lossy where the palette has since grown its own ramp (AntD `orange`, `cyan`
// and `lime` are real tones now) — worth revisiting, behaviour-neutral today.

function antColorToTone(name: string | null | undefined): PillTone {
  switch ((name ?? "").toLowerCase()) {
    case "green":
    case "success":
    case "lime":
      return "green";
    case "red":
    case "error":
    case "volcano":
      return "red";
    case "blue":
    case "geekblue":
    case "primary":
      return "blue";
    case "orange":
    case "gold":
    case "warning":
    case "amber":
      return "amber";
    case "cyan":
    case "teal":
      return "teal";
    case "purple":
    case "magenta":
      return "purple";
    default:
      return "grey";
  }
}

// Per-domain status → tone helpers come from `@ds/tokens/domainTones`.
// `TRANSACTION_STATUS_COLORS` is the canonical source (already typed against
// the DS palette — no AntD-flavoured strings). The comment about the legacy
// `*_COLORS` divergence no longer applies after the 2026-05 consolidation.


// ─── Card title — bigger than KVGrid value, smaller than entity-page H1 ─────

const Title: React.FC<{ children: React.ReactNode; testId?: string }> = ({ children, testId }) => (
  <TextValue
    as="div"
    testId={testId}
    size="lg"
    weight="semibold"
    tone="primary"
    lineHeight="snug"
    wrapWords
  >
    {children}
  </TextValue>
);

// ─── Pill row — small horizontal stack of Pill primitives ────────────────────

const PillRow: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Inline gap="xs" wrap>{children}</Inline>
);

// ─── Header — eyebrow + status pills (same row) + title below ────────────────

interface HeaderProps {
  eyebrow: string;
  title:   React.ReactNode;
  pills?:  React.ReactNode;
}

const Header: React.FC<HeaderProps> = ({ eyebrow, title, pills }) => (
  <Stack gap="xs">
    <Inline gap="sm" align="center" justify="between">
      <Eyebrow>{eyebrow}</Eyebrow>
      {pills && <Box as="span" shrink>{pills}</Box>}
    </Inline>
    <Title>{title}</Title>
  </Stack>
);

// ─── Section — divider + eyebrow + body ──────────────────────────────────────

const SubSection: React.FC<{ label: React.ReactNode; children: React.ReactNode }> = ({
  label,
  children,
}) => (
  // Box owns the top hairline; inner Stack carries the previous gap.
  // Migration drift: the prior raw `marginTop` between sibling SubSections
  // is dropped — the parent `Body` Stack's `gap="sm"` already provides
  // the same visual rhythm.
  <Box paddingY="sm" divider="top">
    <Stack gap="xs">
      <Eyebrow>{label}</Eyebrow>
      {children}
    </Stack>
  </Box>
);

// ─── Card body wrapper — vertical rhythm between sections ────────────────────

const Body: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <Stack gap="sm">{children}</Stack>
);

// ─── Loading + missing helpers ───────────────────────────────────────────────

const Loading: React.FC<{ minWidth?: number }> = ({ minWidth = POPOVER_MIN_WIDTH }) => (
  <Card density="compact" loading style={{ minWidth }} />
);

const Missing: React.FC<{ eyebrow: string; minWidth?: number }> = ({
  eyebrow,
  minWidth = POPOVER_MIN_WIDTH,
}) => (
  <Card density="compact" style={{ minWidth }}>
    <Body>
      <Header eyebrow={eyebrow} title={<EmptyValue />} />
    </Body>
  </Card>
);

// ─── Address helpers (Company) ───────────────────────────────────────────────

function cleanZip(zip: string | null | undefined, country: string | null | undefined): string {
  if (!zip) return "";
  const trimmed = zip.trim();
  if (!country) return trimmed;
  return trimmed.replace(new RegExp(`^${country}\\s*[-\\s]?`, "i"), "").trim();
}

function buildAddressLines(c: {
  street?:       string | null;
  zip_code?:     string | null;
  city?:         string | null;
  country_code?: string | null;
}): string[] {
  const zip      = cleanZip(c.zip_code, c.country_code);
  const cityPart = [zip, c.city].filter(Boolean).join(" ").trim();
  const locLine  = [cityPart, c.country_code].filter(Boolean).join(", ");
  return [c.street || null, locLine || null].filter(Boolean) as string[];
}

// ─── Company ─────────────────────────────────────────────────────────────────

interface CompanyData extends Company {
  parent_company?: Pick<Company, "id" | "short_name"> | null;
}

const CompanySummaryCard: React.FC<{ id: number }> = ({ id }) => {
  const { t } = useTranslation();
  const { query } = useOne<CompanyData>({
    resource: "companies",
    id,
    meta: {
      select:
        "id, short_name, full_name, nip, bdo, street, zip_code, city, country_code, categories, is_pending_review, payment_terms, operating_hours, mpd, mpd_name",
    },
  });
  const { query: contactsQuery } = useList<Contact>({
    resource: "contacts",
    filters: [
      { field: "company_id", operator: "eq", value: id },
      { field: "is_deleted", operator: "eq", value: false },
    ],
    sorters: [{ field: "full_name", order: "asc" }],
    pagination: { mode: "off" },
  });
  const c        = query.data?.data;
  const contacts = contactsQuery.data?.data ?? [];
  const eyebrow  = t("companies.singular", "Firma");

  if (query.isLoading) return <Loading />;
  if (!c)              return <Missing eyebrow={eyebrow} />;

  const addressLines = buildAddressLines(c);
  const mpdLine = c.mpd ? [c.mpd, c.mpd_name].filter(Boolean).join(" · ") : null;

  const meta: KVItem[] = [
    { label: "NIP",  value: <CopyableValue value={c.nip ?? null} format="nip" />, hidden: !c.nip },
    { label: "BDO",  value: <CopyableValue value={c.bdo ?? null} format="bdo" />, hidden: !c.bdo },
    {
      label: t("common.fields.address", "Adres"),
      value: addressLines.length > 0 ? addressLines.join(", ") : <EmptyValue />,
      hidden: addressLines.length === 0,
      fullWidth: true,
    },
    {
      label: t("companies.fields.payment_terms", "Termin"),
      value: c.payment_terms != null ? `${c.payment_terms} dni` : <EmptyValue />,
      hidden: c.payment_terms == null,
    },
    {
      label: t("companies.fields.operating_hours", "Godziny"),
      value: c.operating_hours ?? <EmptyValue />,
      hidden: !c.operating_hours,
    },
    {
      label: "MPD",
      value: mpdLine ?? <EmptyValue />,
      hidden: !mpdLine,
      fullWidth: true,
    },
  ];

  const categoryPills = (c.categories ?? []).map((cat) => (
    <Pill key={cat} tone="blue" size="sm">
      {cat}
    </Pill>
  ));

  const reviewPill = c.is_pending_review ? (
    <Pill tone="amber" size="sm">
      {t("companies.auto_created_warning", "Wymaga weryfikacji")}
    </Pill>
  ) : null;

  return (
    <Card density="compact" style={{ minWidth: POPOVER_MIN_WIDTH }}>
      <Body>
        <Header
          eyebrow={eyebrow}
          title={c.full_name ?? c.short_name}
          pills={reviewPill ? <PillRow>{reviewPill}</PillRow> : undefined}
        />

        {meta.some((m) => !m.hidden) && (
          <KVGrid items={meta} layout="rows" compact testId="entity-summary-meta" />
        )}

        {categoryPills.length > 0 && (
          <SubSection label={t("companies.fields.categories", "Kategorie")}>
            <PillRow>{categoryPills}</PillRow>
          </SubSection>
        )}

        {contacts.length > 0 && (
          <SubSection
            label={`${t("contacts.contacts", "Kontakty")} (${contacts.length})`}
          >
            {contacts.slice(0, 4).map((contact) => (
              <ContactRow key={contact.id} contact={contact} />
            ))}
          </SubSection>
        )}
      </Body>
    </Card>
  );
};

const ContactRow: React.FC<{ contact: Contact }> = ({ contact }) => (
  // Stack `gap="xs"` (4 px) replaces the prior raw 2 px gap. Accepted drift.
  <Stack gap="xs">
    <TextValue
      as="div"
      size="sm"
      weight="semibold"
      tone="primary"
      lineHeight="snug"
    >
      {contact.full_name}
      {contact.job_position && (
        <TextValue as="span" tone="muted" weight="regular">
          {" · "}
          {contact.job_position}
        </TextValue>
      )}
    </TextValue>
    {(contact.phone || contact.email) && (
      <TextValue
        as="div"
        size="xs"
        tone="secondary"
        lineHeight="normal"
      >
        <Inline gap="md" wrap>
          {contact.phone && (
            <CopyableValue value={contact.phone} format="phone" hoverOnly />
          )}
          {contact.email && (
            <CopyableValue value={contact.email} format="email" hoverOnly />
          )}
        </Inline>
      </TextValue>
    )}
  </Stack>
);

// ─── Product ─────────────────────────────────────────────────────────────────

const ProductSummaryCard: React.FC<{ id: number }> = ({ id }) => {
  const { t } = useTranslation();
  const { query } = useOne<Product>({
    resource: "products",
    id,
    meta: {
      select:
        "id, name, waste_code, annex_description, basel_code, hs_code, gtu_code, description",
    },
  });
  const p       = query.data?.data;
  const eyebrow = t("products.singular", "Produkt");

  if (query.isLoading) return <Loading />;
  if (!p)              return <Missing eyebrow={eyebrow} />;

  const codePills: React.ReactNode[] = [];
  if (p.waste_code) {
    codePills.push(
      <Pill key="waste" tone="red" size="sm">
        {p.waste_code}
      </Pill>,
    );
  }
  if (p.basel_code) {
    codePills.push(
      <Pill key="basel" tone="amber" size="sm">
        Basel {p.basel_code}
      </Pill>,
    );
  }
  if (p.hs_code) {
    codePills.push(
      <Pill key="hs" tone="blue" size="sm">
        HS {p.hs_code}
      </Pill>,
    );
  }
  if (p.gtu_code) {
    codePills.push(
      <Pill key="gtu" tone="purple" size="sm">
        GTU {p.gtu_code}
      </Pill>,
    );
  }

  return (
    <Card density="compact" style={{ minWidth: POPOVER_MIN_WIDTH }}>
      <Body>
        <Header
          eyebrow={eyebrow}
          title={p.name}
          pills={codePills.length > 0 ? <PillRow>{codePills}</PillRow> : undefined}
        />
        {p.annex_description && (
          <TextValue
            as="div"
            size="sm"
            tone="secondary"
            lineHeight="normal"
          >
            {p.annex_description}
          </TextValue>
        )}
      </Body>
    </Card>
  );
};

// ─── Transaction ─────────────────────────────────────────────────────────────

interface TransactionSummary extends Transaction {
  product?:              Pick<Product, "id" | "name"> | null;
  load_place_company?:   Pick<Company, "id" | "short_name"> | null;
  unload_place_company?: Pick<Company, "id" | "short_name"> | null;
}

const TransactionSummaryCard: React.FC<{ id: number }> = ({ id }) => {
  const { t } = useTranslation();
  const { weight, date, currency: fmtCurrency } = useFormat();
  const { query } = useOne<TransactionSummary>({
    resource: "transactions_with_checks",
    id,
    meta: {
      select: `id, reference_number, status_override, load_date, unload_date, loaded_weight, unloaded_weight,
        load_price, load_price_currency, load_price_type, unload_price, unload_price_currency, unload_price_type,
        product:product_id(id, name),
        load_place_company:load_place_company_id(id, short_name),
        unload_place_company:unload_place_company_id(id, short_name)`,
    },
  });
  const tx      = query.data?.data;
  const eyebrow = t("transactions.singular", "Transakcja");

  if (query.isLoading) return <Loading />;
  if (!tx)             return <Missing eyebrow={eyebrow} />;

  const statusPill = tx.status_override ? (
    <Pill tone={TRANSACTION_STATUS_COLORS[tx.status_override as keyof typeof TRANSACTION_STATUS_COLORS] ?? "grey"} size="sm">
      {t(`transactions.status.${tx.status_override}`, tx.status_override)}
    </Pill>
  ) : null;

  const price        = tx.load_price ?? tx.unload_price;
  const priceCcy     = tx.load_price_currency ?? tx.unload_price_currency;
  const priceType    = tx.load_price_type ?? tx.unload_price_type;
  const priceSuffix  = priceType === "per_ton" ? " / t" : undefined;
  const massValue    = tx.loaded_weight ?? tx.unloaded_weight ?? null;

  const meta: KVItem[] = [
    {
      label:    t("transactions.fields.product", "Produkt"),
      value:    tx.product?.name ?? <EmptyValue />,
      hidden:   !tx.product,
      fullWidth: true,
    },
    {
      label:  t("transactions.fields.load_place", "Załadunek"),
      value:  tx.load_place_company
        ? `${tx.load_place_company.short_name}${tx.load_date ? " · " + date(tx.load_date) : ""}`
        : <EmptyValue />,
      hidden: !tx.load_place_company,
    },
    {
      label:  t("transactions.fields.unload_place", "Rozładunek"),
      value:  tx.unload_place_company
        ? `${tx.unload_place_company.short_name}${tx.unload_date ? " · " + date(tx.unload_date) : ""}`
        : <EmptyValue />,
      hidden: !tx.unload_place_company,
    },
    {
      label:  t("transactions.fields.loaded_weight", "Masa"),
      value:  massValue != null ? weight(massValue) : <EmptyValue />,
      hidden: massValue == null,
    },
    {
      label:  t("transactions.fields.load_price", "Cena"),
      value:  price != null
        ? <AmountDisplay value={price} currency={priceCcy} suffix={priceSuffix} size="sm" />
        : <EmptyValue />,
      hidden: price == null,
      // Avoid the "string title tooltip" path — use renderValue for the AmountDisplay branch
      renderValue: price != null
        ? () => <AmountDisplay value={price} currency={priceCcy} suffix={priceSuffix} size="sm" />
        : undefined,
    },
  ];

  // formatCurrency is referenced indirectly via AmountDisplay; suppress unused warning.
  void fmtCurrency;

  return (
    <Card density="compact" style={{ minWidth: POPOVER_MIN_WIDTH }}>
      <Body>
        <Header
          eyebrow={eyebrow}
          title={
            <CopyableValue
              value={tx.reference_number}
              format="reference"
            />
          }
          pills={statusPill ? <PillRow>{statusPill}</PillRow> : undefined}
        />
        {meta.some((m) => !m.hidden) && (
          <KVGrid items={meta} layout="rows" compact testId="entity-summary-meta" />
        )}
      </Body>
    </Card>
  );
};

// ─── Invoice ─────────────────────────────────────────────────────────────────

interface InvoiceSummaryData extends InvoiceRow {
  seller_company?: Pick<Company, "id" | "short_name"> | null;
  buyer_company?:  Pick<Company, "id" | "short_name"> | null;
}

const InvoiceSummaryCard: React.FC<{ id: number }> = ({ id }) => {
  const { t } = useTranslation();
  const { date } = useFormat();
  const { query } = useOne<InvoiceSummaryData>({
    resource: "invoices",
    id,
    meta: {
      select: `id, invoice_number, invoice_type, direction, review_status, gross_amount, net_amount, currency,
        invoice_date, payment_date,
        seller_company:seller_company_id(id, short_name),
        buyer_company:buyer_company_id(id, short_name)`,
    },
  });
  const inv     = query.data?.data;
  const eyebrow = t("invoices.singular", "Faktura");

  if (query.isLoading) return <Loading />;
  if (!inv)            return <Missing eyebrow={eyebrow} />;

  const sellerCompany = inv.seller_company;
  const buyerCompany  = inv.buyer_company;

  const directionPill = inv.direction ? (
    <EnumChip kind="invoice_direction" value={inv.direction} size="sm" />
  ) : null;
  const reviewPill = inv.review_status ? (
    <StatusChipShell
      tone={INVOICE_REVIEW_STATUS_COLORS[inv.review_status] ?? "grey"}
      label={t(`invoices.review_status.${inv.review_status}`, inv.review_status)}
      size="sm"
    />
  ) : null;

  const meta: KVItem[] = [
    {
      label:  t("invoices.fields.gross_amount", "Brutto"),
      value:  inv.gross_amount,
      hidden: inv.gross_amount == null,
      renderValue: () => (
        <AmountDisplay
          value={inv.gross_amount}
          currency={inv.currency}
          colored
          strong
          size="md"
        />
      ),
    },
    {
      label:  t("invoices.fields.net_amount", "Netto"),
      value:  inv.net_amount,
      hidden: inv.net_amount == null,
      renderValue: () => (
        <AmountDisplay value={inv.net_amount} currency={inv.currency} size="sm" />
      ),
    },
    {
      label:  t("invoices.fields.invoice_date", "Data"),
      value:  inv.invoice_date ? date(inv.invoice_date) : <EmptyValue />,
      hidden: !inv.invoice_date,
    },
    {
      label:  t("invoices.fields.payment_date", "Termin"),
      value:  inv.payment_date ? date(inv.payment_date) : <EmptyValue />,
      hidden: !inv.payment_date,
    },
    {
      label:  t("invoices.fields.seller_company", "Sprzedawca"),
      value:  inv.seller_company?.short_name ?? <EmptyValue />,
      hidden: !inv.seller_company,
      fullWidth: true,
      renderValue: sellerCompany
        ? () => (
          <EntityChip
            label={sellerCompany.short_name}
            tone="blue"
            arrow={false}
            size="sm"
            icon={<ShopOutlined />}
          />
        )
        : undefined,
    },
    {
      label:  t("invoices.fields.buyer_company", "Nabywca"),
      value:  inv.buyer_company?.short_name ?? <EmptyValue />,
      hidden: !inv.buyer_company,
      fullWidth: true,
      renderValue: buyerCompany
        ? () => (
          <EntityChip
            label={buyerCompany.short_name}
            tone="green"
            arrow={false}
            size="sm"
            icon={<ShopOutlined />}
          />
        )
        : undefined,
    },
  ];

  const pillRow = directionPill || reviewPill ? (
    <PillRow>
      {directionPill}
      {reviewPill}
    </PillRow>
  ) : undefined;

  return (
    <Card density="compact" style={{ minWidth: POPOVER_MIN_WIDTH }}>
      <Body>
        <Header
          eyebrow={eyebrow}
          title={<CopyableValue value={inv.invoice_number} format="invoiceNumber" />}
          pills={pillRow}
        />
        {meta.some((m) => !m.hidden) && (
          <KVGrid items={meta} layout="rows" compact testId="entity-summary-meta" />
        )}
      </Body>
    </Card>
  );
};

// ─── Employee ────────────────────────────────────────────────────────────────

const EmployeeSummaryCard: React.FC<{ id: number }> = ({ id }) => {
  const { t } = useTranslation();
  const { query } = useOne<Employee>({
    resource: "employees",
    id,
    meta: { select: "id, full_name, email, phone, position, roles" },
  });
  const emp     = query.data?.data;
  const eyebrow = t("employees.singular", "Pracownik");

  if (query.isLoading) return <Loading />;
  if (!emp)            return <Missing eyebrow={eyebrow} />;

  const rolePills = (emp.roles ?? []).map((role) => (
    <StatusChipShell
      key={role}
      tone={ROLE_COLORS[role] ?? "grey"}
      label={t(`employees.roles.${role}`, role)}
      size="sm"
    />
  ));

  const meta: KVItem[] = [
    {
      label:  t("employees.fields.position", "Stanowisko"),
      value:  emp.position ?? <EmptyValue />,
      hidden: !emp.position,
      fullWidth: true,
    },
    {
      label:  t("employees.fields.email", "Email"),
      value:  emp.email,
      hidden: !emp.email,
      renderValue: emp.email
        ? () => <CopyableValue value={emp.email} format="email" hoverOnly />
        : undefined,
      fullWidth: true,
    },
    {
      label:  t("employees.fields.phone", "Telefon"),
      value:  emp.phone,
      hidden: !emp.phone,
      renderValue: emp.phone
        ? () => <CopyableValue value={emp.phone} format="phone" hoverOnly />
        : undefined,
    },
  ];

  return (
    <Card density="compact" style={{ minWidth: POPOVER_MIN_WIDTH }}>
      <Body>
        <Header
          eyebrow={eyebrow}
          title={emp.full_name}
          pills={rolePills.length > 0 ? <PillRow>{rolePills}</PillRow> : undefined}
        />
        {meta.some((m) => !m.hidden) && (
          <KVGrid items={meta} layout="rows" compact testId="entity-summary-meta" />
        )}
      </Body>
    </Card>
  );
};

// ─── Price List ──────────────────────────────────────────────────────────────

interface PriceListSummary extends PriceList {
  company?:      Pick<Company, "id" | "short_name"> | null;
  product?:      Pick<Product, "id" | "name"> | null;
  load_place?:   Pick<Company, "id" | "short_name"> | null;
  unload_place?: Pick<Company, "id" | "short_name"> | null;
}

const PriceListSummaryCard: React.FC<{ id: number }> = ({ id }) => {
  const { t } = useTranslation();
  const { date } = useFormat();
  const { query } = useOne<PriceListSummary>({
    resource: "price_lists",
    id,
    meta: {
      select: `id, name, side, amount, currency, price_type, vat_rate_code, incoterms, valid_from, valid_to, request_dpr, gd_status,
        company:company_id(id, short_name),
        product:product_id(id, name),
        load_place:load_place_company_id(id, short_name),
        unload_place:unload_place_company_id(id, short_name)`,
    },
  });
  const pl      = query.data?.data;
  const eyebrow = t("price_lists.singular", "Cennik");

  if (query.isLoading) return <Loading />;
  if (!pl)             return <Missing eyebrow={eyebrow} />;

  const sidePill = pl.side ? (
    <SideChip key="side" side={pl.side} size="sm" />
  ) : null;
  const priceTypePill = pl.price_type ? (
    <EnumChip key="price_type" kind="price_type" value={pl.price_type} size="sm" />
  ) : null;
  const incotermsPill = pl.incoterms ? (
    <EnumChip key="incoterms" kind="incoterms" value={pl.incoterms} size="sm" />
  ) : null;
  const dprPill = pl.request_dpr ? (
    <DprChip key="dpr" active={pl.request_dpr} size="sm" />
  ) : null;

  const displayRef = formatPriceListRef(pl);

  const meta: KVItem[] = [
    {
      label:  t("price_lists.fields.product", "Produkt"),
      value:  pl.product?.name ?? <EmptyValue />,
      hidden: !pl.product,
      fullWidth: true,
    },
    {
      label:  t("price_lists.fields.company", "Kontrahent"),
      value:  pl.company?.short_name ?? <EmptyValue />,
      hidden: !pl.company,
      fullWidth: true,
    },
    {
      label:  "VAT",
      value:  formatVatRateCode(pl.vat_rate_code),
    },
    {
      label:  t("price_lists.fields.valid_from", "Ważność"),
      value:  `${date(pl.valid_from)} – ${date(pl.valid_to)}`,
      hidden: !pl.valid_from && !pl.valid_to,
    },
    {
      label:  t("price_lists.fields.load_place", "Załadunek"),
      value:  pl.load_place?.short_name ?? <EmptyValue />,
      hidden: !pl.load_place,
    },
    {
      label:  t("price_lists.fields.unload_place", "Rozładunek"),
      value:  pl.unload_place?.short_name ?? <EmptyValue />,
      hidden: !pl.unload_place,
    },
  ];

  const pills = [priceTypePill, incotermsPill, dprPill, sidePill].filter(Boolean);

  return (
    <Card density="compact" style={{ minWidth: POPOVER_MIN_WIDTH }}>
      <Body>
        <Header
          eyebrow={eyebrow}
          title={
            <Inline as="span" inline gap="sm" align="baseline" wrap>
              <AmountDisplay
                value={pl.amount}
                currency={pl.currency}
                suffix={pl.price_type === "per_ton" ? " / t" : undefined}
                size="md"
                strong
              />
              <TextValue as="span" size="xs" tone="muted" weight="regular">
                {displayRef}
              </TextValue>
            </Inline>
          }
          pills={pills.length > 0 ? <PillRow>{pills}</PillRow> : undefined}
        />
        {meta.some((m) => !m.hidden) && (
          <KVGrid items={meta} layout="rows" compact testId="entity-summary-meta" />
        )}
      </Body>
    </Card>
  );
};

// ─── EDPR Document ───────────────────────────────────────────────────────────

const EdprDocumentSummaryCard: React.FC<{ id: number }> = ({ id }) => {
  const { t } = useTranslation();
  const { date } = useFormat();
  const { query } = useOne<{
    id: number;
    edpr_number: string;
    status: string;
    issue_date: string | null;
  }>({
    resource: "edpr_documents",
    id,
    meta: { select: "id, edpr_number, status, issue_date" },
  });
  const doc     = query.data?.data;
  const eyebrow = "EDPR";

  if (query.isLoading) return <Loading />;
  if (!doc)            return <Missing eyebrow={eyebrow} />;

  const statusPill = doc.status ? (
    <Pill tone={antColorToTone(doc.status)} size="sm">
      {doc.status}
    </Pill>
  ) : null;

  const meta: KVItem[] = [
    {
      label:  t("edpr.fields.edpr_issue_date", "Data wydania"),
      value:  doc.issue_date ? date(doc.issue_date) : <EmptyValue />,
      hidden: !doc.issue_date,
    },
  ];

  return (
    <Card density="compact" style={{ minWidth: POPOVER_MIN_WIDTH }}>
      <Body>
        <Header
          eyebrow={eyebrow}
          title={<CopyableValue value={doc.edpr_number} format="reference" />}
          pills={statusPill ? <PillRow>{statusPill}</PillRow> : undefined}
        />
        {meta.some((m) => !m.hidden) && (
          <KVGrid items={meta} layout="rows" compact testId="entity-summary-meta" />
        )}
      </Body>
    </Card>
  );
};

// ─── EDPR Request ────────────────────────────────────────────────────────────

const EdprRequestSummaryCard: React.FC<{ id: number }> = ({ id }) => {
  const { t } = useTranslation();
  const { weight } = useFormat();
  const { query } = useOne<{
    id: number;
    request_number: string;
    applicant_company: string | null;
    waste_code: string | null;
    requested_mass: number | null;
    status: string | null;
    request_date: string | null;
  }>({
    resource: "edpr_requests",
    id,
    meta: {
      select: "id, request_number, applicant_company, waste_code, requested_mass, status, request_date",
    },
  });
  const req     = query.data?.data;
  const eyebrow = t("edpr.request_singular", "Wniosek EDPR");

  if (query.isLoading) return <Loading />;
  if (!req)            return <Missing eyebrow={eyebrow} />;

  const pills: React.ReactNode[] = [];
  if (req.waste_code) {
    pills.push(<Pill key="waste" tone="red" size="sm">{req.waste_code}</Pill>);
  }
  if (req.status) {
    pills.push(
      <Pill key="status" tone="grey" size="sm">{req.status}</Pill>,
    );
  }

  const meta: KVItem[] = [
    {
      label:  t("edpr.fields.applicant_company", "Wnioskujący"),
      value:  req.applicant_company ?? <EmptyValue />,
      hidden: !req.applicant_company,
      fullWidth: true,
    },
    {
      label:  t("edpr.fields.requested_mass", "Masa"),
      value:  req.requested_mass != null ? weight(req.requested_mass) : <EmptyValue />,
      hidden: req.requested_mass == null,
    },
  ];

  return (
    <Card density="compact" style={{ minWidth: POPOVER_MIN_WIDTH }}>
      <Body>
        <Header
          eyebrow={eyebrow}
          title={<CopyableValue value={req.request_number} format="reference" />}
          pills={pills.length > 0 ? <PillRow>{pills}</PillRow> : undefined}
        />
        {meta.some((m) => !m.hidden) && (
          <KVGrid items={meta} layout="rows" compact testId="entity-summary-meta" />
        )}
      </Body>
    </Card>
  );
};

// ─── Bank batch ──────────────────────────────────────────────────────────────

const BankBatchSummaryCard: React.FC<{ id: number }> = ({ id }) => {
  const { t } = useTranslation();
  const { date } = useFormat();
  const { query } = useOne<{
    id: number;
    client_order_group_id: string | null;
    debtor_iban: string | null;
    debtor_name: string | null;
    currency: string | null;
    total_amount: number | null;
    status: string | null;
    sent_at: string | null;
    settled_at: string | null;
    denial_code: string | null;
  }>({
    resource: "mbank_outbound_batch",
    id,
    meta: {
      select:
        "id, client_order_group_id, debtor_iban, debtor_name, currency, total_amount, status, sent_at, settled_at, denial_code",
    },
  });
  const b       = query.data?.data;
  const eyebrow = t("bank.batch.singular", "Paczka");

  if (query.isLoading) return <Loading />;
  if (!b)              return <Missing eyebrow={eyebrow} />;

  const statusPill = b.status ? (
    <Pill tone={antColorToTone(b.status)} size="sm">
      {b.status}
    </Pill>
  ) : null;

  const meta: KVItem[] = [
    {
      label:  t("bank.batch.total_amount", "Kwota"),
      value:  b.total_amount,
      hidden: b.total_amount == null,
      renderValue: b.total_amount != null
        ? () => (
          <AmountDisplay
            value={b.total_amount}
            currency={b.currency ?? "PLN"}
            strong
            size="md"
          />
        )
        : undefined,
    },
    {
      label:  t("bank.batch.debtor_name", "Zleceniodawca"),
      value:  b.debtor_name ?? <EmptyValue />,
      hidden: !b.debtor_name,
      fullWidth: true,
    },
    {
      label:  t("bank.batch.debtor_iban", "IBAN"),
      value:  b.debtor_iban,
      hidden: !b.debtor_iban,
      renderValue: b.debtor_iban
        ? () => <CopyableValue value={b.debtor_iban} format="iban" />
        : undefined,
      fullWidth: true,
    },
    {
      label:  t("bank.batch.sent_at", "Wysłana"),
      value:  b.sent_at ? date(b.sent_at) : <EmptyValue />,
      hidden: !b.sent_at,
    },
    {
      label:  t("bank.batch.settled_at", "Rozliczona"),
      value:  b.settled_at ? date(b.settled_at) : <EmptyValue />,
      hidden: !b.settled_at,
    },
  ];

  return (
    <Card density="compact" style={{ minWidth: POPOVER_MIN_WIDTH }}>
      <Body>
        <Header
          eyebrow={eyebrow}
          title={<CopyableValue value={String(b.id)} format="reference" />}
          pills={statusPill ? <PillRow>{statusPill}</PillRow> : undefined}
        />
        {meta.some((m) => !m.hidden) && (
          <KVGrid items={meta} layout="rows" compact testId="entity-summary-meta" />
        )}
      </Body>
    </Card>
  );
};

// ─── Bank statement line ─────────────────────────────────────────────────────

const BankStatementLineSummaryCard: React.FC<{ id: number }> = ({ id }) => {
  const { t } = useTranslation();
  const { date } = useFormat();
  const { query } = useOne<{
    id: number;
    amount: number | null;
    currency: string | null;
    direction: string | null;
    counterparty_iban: string | null;
    counterparty_name: string | null;
    booking_date: string | null;
    value_date: string | null;
    end_to_end_id: string | null;
    unstructured_remittance: string | null;
    match_state: string | null;
  }>({
    resource: "mbank_statement_line",
    id,
    meta: {
      select:
        "id, amount, currency, direction, counterparty_iban, counterparty_name, booking_date, value_date, end_to_end_id, unstructured_remittance, match_state",
    },
  });
  const l       = query.data?.data;
  const eyebrow = t("bank.line.singular", "Pozycja wyciągu");

  if (query.isLoading) return <Loading />;
  if (!l)              return <Missing eyebrow={eyebrow} />;

  const matchPill = l.match_state ? (
    <Pill tone={antColorToTone(l.match_state)} size="sm">
      {l.match_state}
    </Pill>
  ) : null;

  const signedAmount =
    l.amount != null
      ? (l.direction === "debit" ? -Math.abs(l.amount) : l.amount)
      : null;

  const meta: KVItem[] = [
    {
      label:  t("bank.line.counterparty", "Kontrahent"),
      value:  l.counterparty_name ?? <EmptyValue />,
      hidden: !l.counterparty_name,
      fullWidth: true,
    },
    {
      label:  t("bank.line.counterparty_iban", "IBAN"),
      value:  l.counterparty_iban,
      hidden: !l.counterparty_iban,
      renderValue: l.counterparty_iban
        ? () => <CopyableValue value={l.counterparty_iban} format="iban" />
        : undefined,
      fullWidth: true,
    },
    {
      label:  t("bank.line.booking_date", "Data księgowania"),
      value:  l.booking_date ? date(l.booking_date) : <EmptyValue />,
      hidden: !l.booking_date,
    },
    {
      label:  "E2E",
      value:  l.end_to_end_id,
      hidden: !l.end_to_end_id,
      renderValue: l.end_to_end_id
        ? () => <CopyableValue value={l.end_to_end_id} format="endToEndId" />
        : undefined,
    },
    {
      label:  t("bank.line.remittance", "Tytuł"),
      value:  l.unstructured_remittance ?? <EmptyValue />,
      hidden: !l.unstructured_remittance,
      fullWidth: true,
    },
  ];

  return (
    <Card density="compact" style={{ minWidth: POPOVER_MIN_WIDTH }}>
      <Body>
        <Header
          eyebrow={eyebrow}
          title={
            signedAmount != null ? (
              <AmountDisplay
                value={signedAmount}
                currency={l.currency ?? "PLN"}
                colored
                strong
                sign="actual"
                size="md"
              />
            ) : (
              <EmptyValue />
            )
          }
          pills={matchPill ? <PillRow>{matchPill}</PillRow> : undefined}
        />
        {meta.some((m) => !m.hidden) && (
          <KVGrid items={meta} layout="rows" compact testId="entity-summary-meta" />
        )}
      </Body>
    </Card>
  );
};

// ─── Dispatcher ──────────────────────────────────────────────────────────────

export const EntitySummaryCard: React.FC<{ type: EntityType; id: number }> = ({
  type,
  id,
}) => {
  switch (type) {
    case "company":            return <CompanySummaryCard id={id} />;
    case "product":            return <ProductSummaryCard id={id} />;
    case "transaction":
    case "transaction_v2":     return <TransactionSummaryCard id={id} />;
    case "invoice":
    case "invoice_v2":
    case "invoice_reconcile":  return <InvoiceSummaryCard id={id} />;
    case "employee":           return <EmployeeSummaryCard id={id} />;
    case "price_list":         return <PriceListSummaryCard id={id} />;
    case "edpr_document":      return <EdprDocumentSummaryCard id={id} />;
    case "edpr_request":       return <EdprRequestSummaryCard id={id} />;
    case "bank_batch":         return <BankBatchSummaryCard id={id} />;
    case "bank_statement_line": return <BankStatementLineSummaryCard id={id} />;
    default:                   return null;
  }
};
